<template>
  <article>
    <activity-stage
      :customRootContainerClass="'beginning-letter'"
      :customContentContainerStyles="{
        height: 'calc(100% - 55px)'
      }"
    >
      <template slot="instructions">
        <slot name="instructions"></slot>
      </template>
      <section slot="content" class="focusable" tabindex="0" id="beginning-letter-wrapper">
        <div
          v-if="activityData && currentRound"
          class="round-image-container "
          id="beginning-letter"
        >
          <section class="image-wrapper">
            <img
              :src="currentRound.answerImage?.image?.src"
              :alt="currentRound.answer"
              class="round-image"
            />
            <figcaption v-if="showAnswer" v-text="imageName"></figcaption>
          </section>
          <span
            v-if="!hideKey && !showAnswer"
            :class="['current-key-container keyStyle ', `to-${getKeyType()}`, getKeyColor()]"
            v-text="currentRound.letter"
          ></span>
        </div>
      </section>
    </activity-stage>
    <slot name="keyboard"></slot>
  </article>
</template>

<script>
import { TweenMax, TimelineLite, Back } from 'gsap';

import ActivityStage from 'components/activity/ActivityStage.vue';
import AssetLoader from 'mixins/AssetLoader';
import ErrorLogger from 'mixins/ErrorLogger';
import AccessibilityHelper from 'mixins/AccessibilityHelper';
import TypingActivityHelper from 'mixins/TypingActivityHelper';
import ActivityManager from 'mixins/ActivityManager';

export default {
  mixins: [AssetLoader, TypingActivityHelper, ActivityManager, ErrorLogger, AccessibilityHelper],

  components: {
    ActivityStage
  },
  data() {
    return {
      activityName: 'BeginningLetter',
      sfx: null,
      showAnswer: false,
      hideKey: false,
      isProcessingStep: false,
      growingValues: ['0.1', '0.25', '0.7', '0.75', '1'],
      shrinkingValues: ['5', '4', '3', '2', '1']
    };
  },

  computed: {
    imageName() {
      const { answer } = this.currentRound;
      const regEx = /[^A-Za-z\s*-*_]/gi;

      return answer
        .replace(regEx, '')
        .split('_')
        .join(' ');
    },
    shouldEndRound() {
      return this.progress.stepIndex >= 3;
    }
  },

  beforeDestroy() {
    TweenMax.killAll();
  },

  mounted() {
    this.$watch(
      //@ts-ignore
      () => [this.progress.roundIndex],
      () => {
        this.showAnswer = false;
        this.handleScaling();
      },
      {
        immediate: true
      }
    );
  },

  methods: {
    async loadActivity() {
      this.createRounds({ instructionsClass: 'zoom-in-zoom-out-instructions' });
      await this.loadImages();
      this.sfx = await this.loadSounds();
    },

    async loadImages() {
      this.rounds.forEach(({ answerImage }) => this.queueImage(answerImage?.image?.src));
      return this.downloadImages();
    },

    async loadSounds() {
      const soundPaths = this.rounds.map(({ answerAudio }) => answerAudio.audio.src);
      this.queueAudio(soundPaths);

      const cache = await this.downloadAudio();
      return this.rounds.map(({ answerAudio }) => cache.get(answerAudio.audio.src));
    },

    getKeyboardConfig() {
      const { characters, keyboardType, roofs } = this.activityData.keyboard;
      const isTouchHold = !!characters;
      const showTargets = this.isMobile && !(this.ipadWithoutExternalKeyboard && isTouchHold);

      return {
        showTargets,
        keyboardType,
        roofs
      };
    },

    startActivity() {
      this.setupNewRound({ stepCallback: this.stepCallback });
      this.enableKeyboard();
      this.errorLoggerStart();

      this.$nextTick(this.setActivityContainerHeight);

      this.$nextTick(() => {
        TweenMax.to('.activity-stage', 0.5, { opacity: 1 });
      });
    },

    handleCorrectKey(attempt) {
      if (this.hideKey || this.isProcessingStep) return;

      this.sndType();
      this.rerenderScreenReaderAlert();

      this.captureKeyboardAttemptSegment(attempt, true);

      this.setCurrentKeyboardingSegment('');
      this.setTotalKeyboardingSegment(attempt.character);

      this.handleStepEnd();
      document.getElementById('beginning-letter')?.blur();
    },

    stepCallback(rootGetters) {
      return rootGetters['activity/currentRound']['letter'];
    },

    handleStepEnd() {
      this.isProcessingStep = true;
      if (this.shouldEndRound) {
        this.handleScaling();
        setTimeout(() => {
          this.rerenderScreenReaderAlert(this.imageName);
          this.showAnswer = true;
        }, 800);
      }
      const timeline = new TimelineLite();
      timeline.delay(0.1).call(this.finishStep);
    },

    finishStep() {
      this.completeStep();
      this.setCurrentStep({ stepCallback: this.stepCallback });
      this.handleScaling();
      this.$nextTick(() => {
        if (this.progress.stepIndex >= 4) {
          this.sfx[this.progress.roundIndex].play();
          this.handleRoundEnd();
        } else {
          this.isProcessingStep = false;
        }
      });
    },

    handleRoundEnd() {
      if (!this.isEndOfActivity) {
        const timeline = new TimelineLite();
        timeline.delay(2.5).call(() => {
          this.isProcessingStep = false;
          this.setupNewRound({ stepCallback: this.stepCallback });
        });
      } else {
        const timeline = new TimelineLite();
        timeline.delay(2.5).call(this.completeActivity);
      }
    },

    handleScaling() {
      const isGrowing = this.currentRound.startMinimized;
      const valuesToUse = isGrowing ? this.growingValues : this.shrinkingValues;
      const zoomVal = valuesToUse[this.progress.stepIndex];

      if (this.progress.stepIndex === 0) {
        TweenMax.set('.round-image', {
          css: { scale: zoomVal }
        });
      } else {
        this.hideKey = true;
        TweenMax.to('.round-image', 1, {
          css: { scale: zoomVal },
          ease: Back.easeInOut
        });
        setTimeout(() => {
          this.hideKey = false;
        }, 1000);
      }
    },

    fadeInStep() {
      TweenMax.to(`.roundImage`, 0.5, { opacity: 1 });
    },

    hideAllSteps() {
      TweenMax.set('.step', { opacity: 0 });
      TweenMax.set('.image', { opacity: 0 });
    },
    getKeyType() {
      let keyType = 'normal';
      const { letter } = this.currentRound;
      const classTypes = {
        descend: 'yjpgq',
        ascend: 'kftlbdh'
      };

      Object.entries(classTypes).forEach(([key, value]) => {
        keyType = [...value].includes(letter) ? key : keyType;
      });

      return keyType;
    },
    getKeyColor() {
      let keyColor = '';
      const { letter } = this.currentRound;

      const lettersQueues = {
        qrow: 'qwertyuiop[]',
        arow: "asdfghjkl;'\\",
        zrow: 'zxcvbnm,./'
      };

      Object.entries(lettersQueues).forEach(([key, value]) => {
        if ([...value].includes(letter)) keyColor = key;
      });

      return keyColor;
    }
  },

  watch: {
    currentRound(newVal) {
      if (newVal?.letter) {
        let instructions = `Type the ${newVal.letter} key.`;

        this.setCurrentKeyboardingSegment('');
        this.setTotalKeyboardingSegment(newVal.letter);
        this.setScreenReaderAlert(this.getTouchHoldBasedInstructions(instructions));
      }
    }
  }
};
</script>
